import type React from 'react';
import { cn } from '../../utils/cn.ts';
import { Button } from './Button.tsx';

export interface PaginationControlsProps extends React.HTMLAttributes<HTMLElement> {
    currentPage: number;
    pageCount: number;
    onPageChange: (page: number) => void;
    countLabel?: string;
    totalItems?: number;
    pageSize?: number;
    itemName?: string;
    previousLabel?: string;
    nextLabel?: string;
    disabled?: boolean;
    ariaLabel?: string;
}

function clampPage(currentPage: number, pageCount: number): number {
    return Math.min(Math.max(0, currentPage), Math.max(0, pageCount - 1));
}

function defaultCountLabel(input: {
    currentPage: number;
    itemName: string;
    pageCount: number;
    pageSize?: number;
    totalItems?: number;
}): string {
    const currentPage = clampPage(input.currentPage, input.pageCount);

    if (typeof input.totalItems === 'number' && typeof input.pageSize === 'number') {
        if (input.totalItems === 0) {
            return `No ${input.itemName}`;
        }

        const start = currentPage * input.pageSize + 1;
        const end = Math.min(start + input.pageSize - 1, input.totalItems);
        return `Showing ${start}-${end} of ${input.totalItems}`;
    }

    return `Page ${currentPage + 1} of ${Math.max(1, input.pageCount)}`;
}

export const PaginationControls: React.FC<PaginationControlsProps> = ({
    ariaLabel = 'Pagination',
    className,
    countLabel,
    currentPage,
    disabled = false,
    itemName = 'items',
    nextLabel = 'Next',
    onPageChange,
    pageCount,
    pageSize,
    previousLabel = 'Previous',
    totalItems,
    ...props
}) => {
    const normalizedPageCount = Math.max(1, pageCount);
    const clampedPage = clampPage(currentPage, normalizedPageCount);
    const label = countLabel ?? defaultCountLabel({
        currentPage: clampedPage,
        itemName,
        pageCount: normalizedPageCount,
        pageSize,
        totalItems,
    });

    return (
        <nav
            aria-label={ariaLabel}
            className={cn(
                'flex items-center justify-between gap-3 text-gray-500 text-xs dark:text-gray-400',
                className
            )}
            {...props}
        >
            <span>{label}</span>
            <div className="flex gap-1.5">
                <Button
                    aria-label="Previous page"
                    disabled={disabled || clampedPage === 0}
                    leftIcon="chevron_left"
                    onClick={() => onPageChange(Math.max(0, clampedPage - 1))}
                    size="sm"
                    variant="ghost"
                >
                    {previousLabel}
                </Button>
                <Button
                    aria-label="Next page"
                    disabled={disabled || clampedPage >= normalizedPageCount - 1}
                    onClick={() => onPageChange(Math.min(normalizedPageCount - 1, clampedPage + 1))}
                    rightIcon="chevron_right"
                    size="sm"
                    variant="ghost"
                >
                    {nextLabel}
                </Button>
            </div>
        </nav>
    );
};

PaginationControls.displayName = 'PaginationControls';
